import React, { useEffect, useState } from 'react';
import { Plus, Save, Loader2, CheckCircle2, Edit3, X } from 'lucide-react';
import { api } from '../../lib/api';
import ImageInput from '../../components/admin/ImageInput';
import { Field, TextInput, TextArea, NumberInput, Button } from '../../components/admin/FormControls';

const empty = {
  title: '', day: '', month: '', year: '', location: '', description: '', image_url: '', order: 0
};

const AdminEvents = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get('/events');
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2200); };

  const startNew = () => setEditing({ ...empty, order: (items[items.length - 1]?.order || 0) + 1 });
  const startEdit = (n) => setEditing({ ...n });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        const { id, ...rest } = editing;
        await api.put(`/events/${id}`, rest);
      } else {
        await api.post('/events', editing);
      }
      flash('Saved');
      setEditing(null);
      load();
    } catch (e) {
      flash(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete event "${n.title}"?`)) return;
    await api.delete(`/events/${n.id}`);
    flash('Deleted');
    load();
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#0B2545]">Events</h1>
          <p className="mt-1 text-[13.5px] text-[#0B2545]/65">Manage upcoming workshops, forums and consultations.</p>
        </div>
        <Button variant="gold" onClick={startNew}><Plus size={14} /> Add event</Button>
      </div>

      {msg && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-[13px] text-green-700">
          <CheckCircle2 size={14} /> {msg}
        </div>
      )}

      {loading ? (
        <div className="text-[#0B2545]/60"><Loader2 className="inline animate-spin" size={14} /> Loading...</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((e) => (
            <div key={e.id} className="rounded-lg border border-[#E6DDC8] bg-white p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center rounded-md bg-[#0B2545] px-3 py-2 text-white">
                  <div className="font-serif text-xl font-bold">{e.day}</div>
                  <div className="text-[10px] tracking-widest text-[#C9A86A]">{e.month}</div>
                  <div className="text-[10px] text-white/70">{e.year}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-[16px] font-bold text-[#0B2545]">{e.title}</div>
                  <div className="text-[12.5px] text-[#0B2545]/60">{e.location}</div>
                  <div className="mt-2 line-clamp-2 text-[13px] text-[#0B2545]/75">{e.description}</div>
                  <div className="mt-3 flex items-center gap-2">
                    <Button variant="outline" onClick={() => startEdit(e)}><Edit3 size={12} /> Edit</Button>
                    <button onClick={() => remove(e)} className="text-[12.5px] text-red-600 hover:underline">Delete</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="col-span-full rounded-lg border border-[#E6DDC8] bg-white p-10 text-center text-[#0B2545]/55">
              No events yet.
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between border-b border-[#E6DDC8] bg-white px-6 py-4">
              <h3 className="font-serif text-lg font-bold text-[#0B2545]">{editing.id ? 'Edit event' : 'New event'}</h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="space-y-4 p-6">
              <ImageInput label="Event image" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} height={180} />
              <Field label="Title"><TextInput value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Day" hint="e.g. 15"><TextInput value={editing.day} onChange={(v) => setEditing({ ...editing, day: v })} /></Field>
                <Field label="Month" hint="e.g. DEC"><TextInput value={editing.month} onChange={(v) => setEditing({ ...editing, month: v })} /></Field>
                <Field label="Year" hint="e.g. 2026"><TextInput value={editing.year} onChange={(v) => setEditing({ ...editing, year: v })} /></Field>
              </div>
              <Field label="Location"><TextInput value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} /></Field>
              <Field label="Description"><TextArea rows={4} value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} /></Field>
              <Field label="Order"><NumberInput value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} /></Field>
            </div>
            <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-[#E6DDC8] bg-white px-6 py-4">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving}>{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
